import { useTracking } from 'shared/providers/TrackingProvider';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { PersistedStateKeys } from '../enums';
import { GeneralEvents } from 'shared/providers/TrackingProvider/EventNames';

// A JSON value can be an object, array, number, string, true, false, or null.
// setting and retrieving a type of undefined will cause an error.

const persistedStateInSessionStorage: string[] = [];

const usePersistedState = <T>(
  key: PersistedStateKeys | string,
  defaultValue: T,
  jsonReviver?: ((this: unknown, key: string, value: string) => T) | undefined,
): [T, Dispatch<SetStateAction<T>>] => {
  const { trackEvent } = useTracking();
  let currentState: string | null;
  if (persistedStateInSessionStorage.includes(key)) {
    currentState = sessionStorage.getItem(key);
  } else {
    currentState = localStorage.getItem(key);
  }

  const [persistedState, setPersistedState] = useState<T>(() => {
    return currentState ? JSON.parse(currentState, jsonReviver) : defaultValue;
  });

  /**
   * TODO: Remove reliance on effect hook to sync local storage with local state, since this hook
   * is not run after the calling component is unmounted
   *
   * Example: Navigating away from a conversation after clicking "Send Message" but before the
   * mutation succeeds
   */
  useEffect(() => {
    try {
      if (persistedStateInSessionStorage.includes(key)) {
        sessionStorage.setItem(key, JSON.stringify(persistedState));
      } else {
        localStorage.setItem(key, JSON.stringify(persistedState));
      }
    } catch (error) {
      console.error(error);
      console.error('Clearing LocalStorage');
      trackEvent(GeneralEvents.LocalStorageCleared);
      localStorage.clear();
      sessionStorage.clear();
    }
  }, [key, persistedState, trackEvent]);

  return [persistedState, setPersistedState];
};

export default usePersistedState;
